import type { ButtonHTMLAttributes, ReactNode } from 'react';
import s from './controls.module.css';
import { MoonIcon, PlayIcon, Spinner } from './icons.tsx';

const cx = (...c: (string | false | undefined)[]) => c.filter(Boolean).join(' ');

type Variant = 'primary' | 'secondary' | 'text';

export function Button({
  variant = 'primary',
  loading,
  loadingLabel,
  compact,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  loading?: boolean;
  loadingLabel?: string;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      className={cx(s.btn, s[variant], compact && s.compact, className)}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner label={loadingLabel ?? ''} /> : children}
    </button>
  );
}

/** "▶ Tinglash" pill of the featured card. */
export function PlayPill({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className={s.pill} onClick={onClick}>
      <PlayIcon size={20} />
      <span>{label}</span>
    </button>
  );
}

export function SegmentedControl<V extends string>({
  options,
  value,
  onChange,
  small,
  label,
}: {
  options: { value: V; label: string }[];
  value: V;
  onChange: (v: V) => void;
  small?: boolean;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className={cx(s.segmented, small && s.segmentedSmall)}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={s.segment}
          aria-pressed={o.value === value}
          onClick={() => {
            if (o.value !== value) onChange(o.value);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export type ChipTone = 'free' | 'premium' | 'muted';

export function Chip({
  tone = 'free',
  small,
  children,
}: {
  tone?: ChipTone;
  small?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={cx(
        s.chip,
        tone === 'premium' && s.chipPremium,
        tone === 'muted' && s.chipMuted,
        small && s.chipSmall,
      )}
    >
      {tone === 'premium' && <MoonIcon size={13} filled />}
      {children}
    </span>
  );
}

export function Switch({
  checked,
  onChange,
  id,
  labelledBy,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  id?: string;
  labelledBy?: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      className={s.switch}
      onClick={() => {
        onChange(!checked);
      }}
    >
      <span className={s.knob} />
    </button>
  );
}

export const listStyles = s;
